<template>
  <ButtonAbstract
    v-if="available"
    ref="buttonElement"
    class="button"
    :to="routeLink.path"
    :disabled="disabled"
    :aria-label="category.title"
    @click.capture="handleClick"
  >
    <img v-if="media" :src="media.urlCompressed" />
  </ButtonAbstract>
</template>
<script lang="ts" setup>
import type { CategorySchema } from '~/types/schema';
import {
  getCategoryRouterAction,
  getIsDeepLink,
} from '~/lib/category/actionHelper';

import { categoryDeepLinkParser } from '~/lib/category/deepLinkParser';

import { getServices } from '~/db/services';
interface Props {
  category: CategorySchema;
  index: number;
}

const props = defineProps({
  category: null,
  index: null
});

const { $db } = useNuxtApp();

const { mediasService } = getServices($db);

const media = await mediasService.get({ id: props.category.mediaIcon });

const emit = defineEmits(["hidden"]);

const { currentContent, categoryLockState } = inject(INJECT_CATEGORY_PAGE);

const { all: allUserContents } = useContentsForUserType();

const buttonElement = ref(null);

const route = useRoute();

const isDeepLink = getIsDeepLink(props.category);

let linkedContent: CategorySchema | PostSchema | ExerciseSchema;

let routeLink;

const contents = useContents();

let isLocked: boolean = false;

const available = defineModel<boolean>('available', {
  default: true,
});

const routerAction = await getCategoryRouterAction(
  $db,
  props.category,
  currentContent.value,
  categoryLockState.value.getIsTypeLocked,
  route.path
);

if (isDeepLink || getIsDeepLink(routerAction.linkedContent)) {
  const categoryDeeplink = isDeepLink
    ? props.category
    : (toValue(routerAction.linkedContent) as CategorySchema);
  routeLink = await categoryDeepLinkParser(
    $db,
    deepToRaw(categoryDeeplink),
    contents.all.value,
    contents.mainContent.value,
    route.path === '/'
  );
  if (routeLink.path && routeLink.path.indexOf('/nomad/') === 0) {
    const slug = routeLink.path.split('/nomad/')[1];
    const lbInstalled = allUserContents.value.find(
      (content) => content.slug === slug
    );

    if (lbInstalled) {
      routeLink = { ...routeLink, path: lbInstalled.path };
    }
  }
} else {
  linkedContent = routerAction.linkedContent;
  routeLink = routerAction.routeLink;
  isLocked = routerAction.isLocked;
}

const disabled = computed(() => {
  return isDeepLink && !routeLink.path;
});

const dialogLockedContent = useDialogLockedContent();

const handleClick = (e) => {
  if (toValue(isLocked)) {
    e.preventDefault();
    dialogLockedContent.open(
      linkedContent.contentType,
      null,
      null,
      linkedContent
        ? useAppEventShare(
            EventAssociatedModel.ContentLockScreen,
            linkedContent.id,
            linkedContent.contentType || linkedContent.lockedContent
          )
        : null
    );
  }
};

available.value = isDeepLink ? routeLink.path != null : true;
</script>
<style lang="scss" scoped>
.button {
  display: grid;
  display: flex;
  position: relative;
  align-items: flex-end;

  img {
    z-index: 1;
    transition: transform 300ms $ease-out-expo;
  }
  &:focus,
  &:hover {
    img {
      transform: scale(1.06);
      transition: transform 1500ms $ease-out-expo;
    }
  }
}
</style>
